/* eslint no-use-before-define: "off" */

/**
 * `SessionPersistence` is a pure JS facade over the `ContextHub` object. This
 * pattern allows for the decoupling of the Adobe-specific CH implementation
 * from our interactions with it.
 *
 * @param {(Object|ContextHub)} persistenceTarget
 * @param {string} storeName
 * @return {
    setStoreNamespace: setStoreNamespace,
    setItem: setItem,
    getItem: getItem,
    removeItem: removeItem, targetStore}
 * @constructorra
 */
function SessionPersistence({ persistenceTarget, storeName }) {
  /**
   * We can't do much without a ContextHub instance to work with. After all,
   * this module is basically a nicer facade over ContextHub.
   */
  if (!persistenceTarget) {
    throw new ReferenceError(
      `Missing required parameter \`persistenceTarget\` in constructor.
            Please invoke this module with the following signature:
            new SessionPersistence({ persistenceTarget: Object })`,
    );
  }

  /**
   * Improves the understandability of which parts of this code actually
   * interact with the DI'd ContextHub instance.
   *
   * @type {{getStore: function}}
   */
  const ContextHub = persistenceTarget;

  /**
   * Our reference to a `ContextHub` persistence store
   */
  let targetStore;

  /**
   * This would normally be at the bottom of the constructor, returned inline
   * from this method, but it needs to be initialized before the
   * `setStoreNamespace` method is used, since that needs this to be declared.
   */
  const instanceInterface = {
    setStoreNamespace,
    setItem,
    getItem,
    removeItem,
    has,
    get namespace() {
      return targetStore ? targetStore.name : undefined;
    },
  };

  /**
   * A convenience function to fail fast if the user has not initialised the
   * instance with a store namespace. This can be fixed by calling
   * `setStoreNamespace` on the instance.
   *
   * @private
   */
  function ensureTargetStoreIsSet() {
    if (!targetStore) {
      throw new Error(
        `No target store has been set for this persistence instance.
                Please instantiate the instance with a \`storeName\` constructor
                property or call \`setStoreNamespace\` method after before
                trying to get / set persistent properties.`,
      );
    }
  }

  /**
   * Sets the internal namespace for the persistence object. Use this to
   * store different values under the same key using namespaces. Under the
   * hood, this actually uses separate ContextHub stores, but that is just an
   * interesting implementation detail that consumers of the
   * `SessionPersistence` instance don't need to know.
   *
   * @public
   * @param {string} targetStoreName
   */
  function setStoreNamespace(targetStoreName) {
    if (!targetStoreName) {
      throw new ReferenceError('Missing required parameter `targetStoreName`');
    }
    targetStore = ContextHub.getStore(targetStoreName);
    return instanceInterface;
  }

  /**
   * Store the `value` (serialised to a string) in the persistent store
   * under the key named `key`.
   *
   * @public
   * @param {string} key
   * @param {*} value
   */
  function setItem(key, value) {
    if (!key) {
      throw new ReferenceError('Missing required parameter `key`');
    }

    if (value === undefined) {
      throw new ReferenceError('Missing required parameter `value`');
    }

    ensureTargetStoreIsSet();
    targetStore.setItem(key, value);
    return instanceInterface;
  }

  /**
   * Retrieve the value store in the persistent store (deserialized from a
   * string) stored under the key named `key`. If the key does not exist or
   * there is no value set, returns `null`.
   *
   * @public
   * @param {string} key
   * @returns {(null|*)}
   */
  function getItem(key) {
    ensureTargetStoreIsSet();
    return targetStore.getItem(key);
  }

  /**
   * Remove the value stored under the key named `key` in the persistent
   * store. If the key did not exist (or was set to `null`) this will have
   * no effect. Any further calls to `getItem` for `key` will return `null`.
   *
   * @public
   * @param {string} key
   */
  function removeItem(key) {
    if (!key) {
      throw new ReferenceError('Missing required parameter `key`');
    }

    ensureTargetStoreIsSet();
    targetStore.removeItem(key);
    return instanceInterface;
  }

  /**
   * Determine if the persistent store has a value stored under the key named
   * `key`.
   *
   * Note: if the stored value is `null`, this method will return `false`.
   * There is no way to differentiate between a set-to-`null`-value and a
   * not-set-key.
   *
   * @public
   * @param key
   * @return {boolean}
   */
  function has(key) {
    ensureTargetStoreIsSet();
    return targetStore.getItem(key) !== null;
  }

  /**
   * If constructed with a `storeName` property, we can pre-setup the store
   * ourselves, so the user doesn't have to call `setStoreNamespace` manually,
   * but it's optional so the instance can be inert until required if desired.
   */
  if (storeName) {
    setStoreNamespace(storeName);
  }

  return instanceInterface;
}

export default SessionPersistence;
